import Vue from 'vue';
import types from '../../mutation-types';
import fromUnixTime from 'date-fns/fromUnixTime';
import { format } from 'date-fns';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import router from '../../../routes';
import {
  buildConversationList,
  isOnMentionsView,
  isOnUnattendedView,
} from './helpers/actionHelpers';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';
import forwardQuoteActions from './actions/forwardQuoteActions';
import { conversationListPageURL } from '../../../helper/URLHelper';
import onlineFbActions from './actions/onlineFbActions';
import TiktokClient from '../../../api/channel/tiktokClient';

const messageStamp = (time, dateFormat = 'h:mm a') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, dateFormat);
};

const getMessage = item => {
  if (item.attachments && item.attachments.length > 0) {
    return item.attachments[0].data_url;
  }
  return item.content;
};
// actions
const actions = {
  getConversation: async ({ commit, rootState }, conversationId) => {
    try {
      const response = await ConversationApi.show(conversationId);
      commit(types.UPDATE_CONVERSATION, response.data);
      commit(`contacts/${types.SET_CONTACT_ITEM}`, response.data.meta.sender);
    } catch (error) {
      if (error.response.status === 403) {
        const {
          params: { accountId, inbox_id: inboxId, label, teamId },
        } = rootState.route;
        bus.$emit('newToastMessage', WOOT.$root.$t('CONVERSATION.403'));
        await router.push(
          conversationListPageURL({
            accountId,
            inboxId,
            label,
            teamId,
          })
        );
      }
    }
  },

  fetchAllConversations: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const {
        data: { data },
      } = await ConversationApi.get(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        params.filterKey ?? params.assigneeType
      );
    } catch (error) {
      // Handle error
    }
  },

  fetchFilteredConversations: async ({ commit, dispatch }, params) => {
    if (!params.silent) commit(types.SET_LIST_LOADING_STATUS);
    try {
      const { data } = await ConversationApi.filter(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        'appliedFilters'
      );
      return data;
    } catch (error) {
      // Handle error
    }
  },

  emptyAllConversations({ commit }) {
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit }, data) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages(data);
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: data.conversationId,
        data: payload,
      });
      if (!payload.length) {
        commit(types.SET_ALL_MESSAGES_LOADED);
      }
    } catch (error) {
      // Handle error
    }
  },

  showBefore: ({ commit }, data) => {
    console.log('showBefore action')
    commit(types.SHOW_BEFORE_CONVERSATION, data);
  },
  clearBeforeConversation: ({ commit }) => {
    console.log('clearBeforeConversation action')
    commit(types.CLEAR_BEFORE_CONVERSATION);
  },
  fetchFileMessages: async ({ commit }, id) => {
    try {
      const response = await MessageApi.getFileMessages(id);
      return response.data;
    } catch (error) {
      console.log(error, 'test');
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: data.payload,
      });
    } catch (error) {
      // Handle error
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const { allConversations, syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    try {
      const { messages } = selectedChat;
      // Fetch all the messages after the last message id
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      // Find the messages that are not already present in the store
      const missingMessages = payload.filter(
        message => !messages.find(item => item.id === message.id)
      );
      selectedChat.messages.push(...missingMessages);
      // Sort the messages by created_at
      const sortedMessages = selectedChat.messages.sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: sortedMessages,
      });
      commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
        conversationId,
        messageId: null,
      });
      dispatch('markMessagesRead', { id: conversationId }, { root: true });
    } catch (error) {
      // Handle error
    }
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setActiveChat({ commit, dispatch }, { data, after }) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CLEAR_ALL_MESSAGES_LOADED);
    commit(types.CLEAR_BEFORE_CONVERSATION);
    if (data.dataFetched === undefined) {
      try {
        await dispatch('fetchPreviousMessages', {
          after,
          before: (data.messages.length && data.messages[0].id) || undefined,
          conversationId: data.id,
        });
        Vue.set(data, 'dataFetched', true);
      } catch (error) {
        // Ignore error
      }
    }

    // fix 24 hours restriction update
    try {
      if (data.can_reply) {
        const response = await ConversationApi.show(data.id);
        if (response?.data?.can_reply !== data.can_reply) {
          // to avoid double meta fetch for read status
          commit(types.UPDATE_CONVERSATION, response.data);
        }
      }
    } catch (e) {
      console.log(e);
    }
  },

  assignAgent: async ({ dispatch }, { conversationId, agentId }) => {
    try {
      const response = await ConversationApi.assignAgent({
        conversationId,
        agentId,
      });
      dispatch('setCurrentChatAssignee', response.data);
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatAssignee({ commit }, assignee) {
    commit(types.ASSIGN_AGENT, assignee);
  },

  assignTeam: async ({ dispatch }, { conversationId, teamId }) => {
    try {
      const response = await ConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setCurrentChatTeam', { team: response.data, conversationId });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit },
    { conversationId, status, snoozedUntil = null }
  ) => {
    try {
      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
      });
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: updatedStatus,
        snoozedUntil: updatedSnoozedUntil,
      });
    } catch (error) {
      // Handle error
    }
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    return dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit }, pendingMessage) => {
    try {
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });
      const response = await MessageApi.create(pendingMessage);
      commit(types.ADD_MESSAGE, {
        ...response.data,
        // status: MESSAGE_STATUS.SENT, #why it is here... if operation is success then status comes back from server
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error
        : undefined;
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      throw error;
    }
  },

  addMessage(
    {
      state: { allConversations },
      commit,
      dispatch,
      getters: { canAddConversation },
    },
    message
  ) {
    if (canAddConversation(message.conversation_id)) return;

    commit(types.ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      // d99d otherwise no new messages in old conversations (more than visible conversations)
      const conversationPresent = allConversations.filter(
        c => c.id === message.conversation_id
      ).length;
      if (!conversationPresent) {
        dispatch('getConversation', message.conversation_id);
      }
      // .
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.SET_CONVERSATION_LAST_INCOMING, {
        conversationId: message.conversation_id,
        lastMessageTime: message.updated_at,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
  },

  updateMessage({ commit }, message) {
    commit(types.UPDATE_MESSAGE, message); // d99d change to update
  },

  deleteMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      if (data.delay_hours) {
        commit(types.DELETE_MESSAGE, data);
      } else {
        commit(types.ADD_MESSAGE, data);
      }
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  async addConversation(
    {
      commit,
      state,
      dispatch,
      rootState,
      getters: { canAddConversation, getFoldersId },
      rootGetters,
    },
    conversation
  ) {
    const { currentInbox, appliedFilters } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = conversation;
    if (canAddConversation(conversation.id)) {
      if (!getFoldersId) return;
      const query = rootGetters['customViews/getCustomViews'].find(
        ({ id }) => id == getFoldersId
      );
      if (!query) return;
      dispatch('fetchFilteredConversations', {
        queryData: {
          payload: query.query.payload,
        },
        silent: true,
      });
      return;
    }

    const hasAppliedFilters = !!appliedFilters.length;
    const isMatchingInboxFilter =
      !currentInbox || Number(currentInbox) === inboxId;
    if (
      !hasAppliedFilters &&
      !isOnMentionsView(rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.ADD_CONVERSATION, conversation);
      dispatch('contacts/setContact', sender);
    }
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  async updateConversation(
    { commit, dispatch, getters: { getFoldersId }, rootGetters },
    conversation
  ) {
    const {
      meta: { sender },
    } = conversation;
    if (getFoldersId) {
      const query = rootGetters['customViews/getCustomViews'].find(
        ({ id }) => id == getFoldersId
      );
      if (!query) return;
      const { payload } = await dispatch('fetchFilteredConversations', {
        queryData: {
          payload: query.query.payload,
        },
        silent: true,
      });
      const presentInResponse = payload.find(
        ({ id }) => id === conversation.id
      );
      if (!presentInResponse) commit(types.REMOVE_CONVERSATION, conversation);
      return;
    }
    if (conversation.removed_from_current_list) {
      commit(types.REMOVE_CONVERSATION, conversation);
    } else {
      commit(types.UPDATE_CONVERSATION, conversation);
    }

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    dispatch('contacts/setContact', sender);
  },

  markConversationUnread: async (
    { commit, dispatch, rootState },
    { id, foldersId }
  ) => {
    try {
      const {
        data: {
          agent_last_seen_at: lastSeen,
          assignee_last_seen_at: assigneeLastSeen,
        },
      } = await ConversationApi.markConversationUnread(id);

      commit(types.MARK_MESSAGE_READ, { id, lastSeen, assigneeLastSeen });
      dispatch('setItemMarkedAsUnread', id);

      const {
        params: { accountId, inbox_id: inboxId, label, teamId },
        name,
      } = rootState.route;
      let conversationType = '';
      if (isOnMentionsView({ route: { name } })) {
        conversationType = 'mention';
      } else if (isOnUnattendedView({ route: { name } })) {
        conversationType = 'unattended';
      }
      await router.push(
        conversationListPageURL({
          accountId,
          conversationType: conversationType,
          customViewId: foldersId,
          inboxId,
          label,
          teamId,
        })
      );
    } catch (error) {
      // Ignore error
    }
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  setItemMarkedAsUnread({ commit }, data) {
    commit(types.CHANGE_ITEM_MARKED_AS_UNREAD, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.mute(conversationId);
      commit(types.MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, payload) => {
    try {
      return await ConversationApi.sendEmailTranscript(payload);
    } catch (error) {
      throw new Error(error);
    }
  },

  sendTiktokLike: async (_, payload) => {
    try {
      await TiktokClient.tiktokLike(payload);
    } catch (error) {
      throw new Error(error);
    }
  },

  sendTiktokHide: async (_, payload) => {
    try {
      await TiktokClient.tiktokHide(payload);
    } catch (error) {
      throw new Error(error);
    }
  },

  exportConversations: async ({ commit, dispatch }, params) => {
    try {
      const { data } = await ConversationApi.exportConversations(params);
      return data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error();
      }
      // Handle error
    }
  },

  downloadAllConversations: async ({ commit }) => {
    try {
      await ConversationApi.exportConversations();
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error();
      }
    }
  },

  getExportConversationsStatus: async ({ commit }, params) => {
    const { data } = await ConversationApi.getExportConversationsStatus(params);
    return data;
  },

  statusOfLastDownloadAllConversations: async ({ commit }, inboxId) => {
    const { data } = await ConversationApi.getExportConversationsStatus();
    return data;
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, custom_attributes);
    } catch (error) {
      // Handle error
    }
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  ...messageReadActions,
  ...messageTranslateActions,
  ...forwardQuoteActions,
  ...onlineFbActions, // d99d
};

export default actions;
